import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewEncapsulation,
    OnInit,
} from "@angular/core";
import { S25PipesModule } from "../../../../../pipes/s25.pipes.module";
import { S25CheckboxModule } from "../../../../s25-checkbox/s25.checkbox.module";
import { S25SimpleCollapseModule } from "../../../../s25-simple-collapse/s25.simple.collapse.module";
import { S25Reservation } from "../../../ReservationI";
import { Event } from "../../../../../pojo/Event";
import Rsrv = Event.Reservation.States;
import { EventOccUtil } from "../s25.event.occurrence.util";
import { S25EventOccurrencesService } from "../../s25.event.occurrences.service";
import { S25ProfileUtil } from "../../../utils/s25.profile.util";

@Component({
    selector: "occurrence-set-for-all",
    template: `@if (init) {
        <div>
            <s25-simple-collapse [header]="'Set For All'" [expanded]="false">
                <div class="collapseContainer">
                    <p class="c-margin-left--half" tabindex="0">
                        By selecting occurrences from the below list, you will be able to apply changes to assignments,
                        instructions, shared and comments from one occurrence to all selected occurrences:
                    </p>
                    <div class="checkbox-container">
                        <div>
                            <s25-ng-checkbox
                                [value]="false"
                                [ariaLabel]="'Apply All'"
                                (valueChange)="onSelectAll($event)"
                            ></s25-ng-checkbox>
                            Apply All
                        </div>
                        @for (occ of occurrencesList; track occ; let i = $index) {
                            @if (occ.state !== 99 && occ.state !== 2) {
                                <div>
                                    <s25-ng-shift-selectable-checkbox
                                        [id]="'item-' + i"
                                        [index]="i"
                                        [group]="'occ'"
                                        [value]="selectAll"
                                        [ariaLabel]="occ.eventStart | dateFormat: dateFormat"
                                        (valueChange)="onCheckBoxChange(occ, $event)"
                                    ></s25-ng-shift-selectable-checkbox>
                                    {{ occ.eventStart | dateFormat: dateFormat }}
                                    <span class="small">
                                        ({{ occ.eventStart | timeFormat: timeFormat }} -
                                        {{ occ.eventEnd | timeFormat: timeFormat }})
                                    </span>
                                </div>
                            }
                        }
                    </div>
                </div>
            </s25-simple-collapse>
            <div class="ngBold -margin-left--double">{{ repetitionText }}</div>
        </div>
    }`,
    styles: [
        `
            .collapseContainer {
                padding: 1rem;

                .checkbox-container {
                    display: flex;
                    flex-direction: column;
                    flex-wrap: wrap;
                    gap: 5px;
                    width: 95%;
                    margin: 1rem auto;
                    max-height: 30vh;
                    overflow-y: auto; /* Allow vertical scrolling if content overflows */
                }
            }
        `,
    ],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [S25CheckboxModule, S25SimpleCollapseModule, S25PipesModule],
})
export class OccurrenceSetForAllComponent implements OnInit {
    @Input() occurrencesList?: S25Reservation[];
    @Input() dateFormat: string;
    @Input() timeFormat?: string;
    @Output() selectedRsrv = new EventEmitter<number[]>();
    init: boolean;
    selectedItems: number[] = [];
    selectAll: boolean = false;
    repetitionText: string = "";

    constructor(
        private cd: ChangeDetectorRef,
        private elementRef: ElementRef,
        private occurrencesService: S25EventOccurrencesService,
    ) {}

    async ngOnInit() {
        const profileCode = await S25ProfileUtil.getProfileCode(
            this.occurrencesService.getProfiles(),
            this.occurrencesList[0]?.profileId,
        );
        this.repetitionText = await EventOccUtil.getRepetitionText(profileCode);
        this.init = true;
        this.cd.detectChanges();
    }

    onSelectAll(event: boolean) {
        this.selectAll = event;
        if (event) {
            this.selectedItems =
                this.occurrencesList
                    ?.filter((occ: S25Reservation) => occ.state !== Rsrv.Cancelled && occ.state !== Rsrv.Exception)
                    .map((occ: S25Reservation) => occ.itemId) || [];
        } else {
            this.selectedItems = [];
        }
        this.selectedRsrv.emit(this.selectedItems);
        this.cd.detectChanges();
    }

    onCheckBoxChange(occ: S25Reservation, event: boolean) {
        if (event && !this.selectedItems.includes(occ.itemId)) {
            this.selectedItems.push(occ.itemId);
        } else {
            const index = this.selectedItems.indexOf(occ.itemId);
            if (index !== -1) this.selectedItems.splice(index, 1);
        }
        this.selectedRsrv.emit(this.selectedItems);
        this.cd.detectChanges();
    }
}
